import React, { useEffect, useRef, useMemo } from 'react';
import Gantt from 'frappe-gantt';
import '../../styles/frappe-gantt.css';

/**
 * Wrapper around frappe-gantt for project planning.
 * Props:
 *  - tasks: [{id, name, start, end, progress, custom_class, project }]
 *  - viewMode: 'Day' | 'Week' | 'Month'
 *  - onDateChange(task, start, end)  // fired on drag/resize
 *  - onClick(task)
 */
export const ProjectsGantt = ({ tasks, viewMode = 'Week', onDateChange, onClick }) => {
  const ref = useRef(null);
  const ganttRef = useRef(null);

  // Ensure tasks have at least 1 day of width to be visible & draggable
  const safeTasks = useMemo(() => (tasks || []).map(t => {
    const s = t.start ? new Date(t.start) : new Date();
    let e = t.end ? new Date(t.end) : new Date(s);
    if (!t.end || e < s) {
      e = new Date(s);
      e.setDate(e.getDate() + 1);
    }
    return { ...t, start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) };
  }), [tasks]);

  useEffect(() => {
    if (!ref.current || safeTasks.length === 0) return;
    // Recreate cleanly each render to avoid frappe-gantt internal state issues
    ref.current.innerHTML = '';
    const data = safeTasks.map(t => ({
      id: String(t.id),
      name: t.name,
      start: t.start,
      end: t.end,
      progress: t.progress || 0,
      custom_class: t.custom_class || '',
    }));
    ganttRef.current = new Gantt(ref.current, data, {
      view_mode: viewMode,
      bar_height: 26,
      bar_corner_radius: 4,
      padding: 18,
      language: 'fr',
      date_format: 'YYYY-MM-DD',
      popup_trigger: 'mouseover',
      on_date_change: (task, start, end) => {
        if (onDateChange) {
          const s = start instanceof Date ? start : new Date(start);
          const e = end instanceof Date ? end : new Date(end);
          onDateChange(task, s.toISOString().slice(0, 10), e.toISOString().slice(0, 10));
        }
      },
      on_click: (task) => { if (onClick) onClick(task); },
      custom_popup_html: (task) => {
        const orig = safeTasks.find(t => String(t.id) === String(task.id));
        const client = orig?.project?.client_name || '';
        return `<div class="bb-gantt-popup">
          <div style="font-weight:700;font-size:0.9rem;margin-bottom:4px">${task.name}</div>
          ${client ? `<div style="font-size:0.78rem;color:#5E5F66;margin-bottom:4px">${client}</div>` : ''}
          <div style="font-size:0.78rem">Du <b>${task._start.toLocaleDateString('fr-FR')}</b> au <b>${task._end.toLocaleDateString('fr-FR')}</b></div>
        </div>`;
      },
    });
  }, [safeTasks, viewMode, onDateChange, onClick]);

  return (
    <>
      <div ref={ref} data-testid="projects-gantt" style={{ minHeight: 200, overflow: 'auto' }} />
      {/* Custom styling for our project bar classes */}
      <style>{`
        .bb-gantt-current .bar { fill: #1565C0 !important; }
        .bb-gantt-current .bar-progress { fill: #0D47A1 !important; }
        .bb-gantt-planned .bar { fill: #FB8C00 !important; }
        .bb-gantt-planned .bar-progress { fill: #E65100 !important; }
        .bb-gantt-done .bar { fill: #2E7D32 !important; }
        .bb-gantt-done .bar-progress { fill: #1B5E20 !important; }
        .bb-gantt-leave .bar { fill: #9CA3AF !important; opacity: 0.55; }
        .bb-gantt-leave .bar-progress { fill: #6B7280 !important; opacity: 0.7; }
        .bb-gantt-leave .bar-label { fill: #1F2937 !important; font-weight: 500; font-style: italic; }
        .bb-gantt-popup { padding: 8px 12px; max-width: 260px; }
        .gantt .bar-label { fill: white !important; font-weight: 600; }
      `}</style>
    </>
  );
};
